import { useCallback, useState } from 'react';
import { api, type PlatformStatus } from '../../lib/api.js';
import { Skeleton } from '@threatlens/design-system';
import { usePoll } from '../../lib/use-poll.js';

/**
 * Live platform figures on the homepage.
 *
 * Every number here is measured, and a number that cannot be measured renders "n/a" rather than 0.
 * That distinction is the whole point: zero is a legitimate reading (nothing is detonating right
 * now) and an unmeasured tier displayed as zero would be a false claim about the platform. The
 * server encodes this by typing the unmeasurable fields as `number | null`.
 */

function compact(n: number): string {
  if (n < 1_000) return String(n);
  if (n < 1_000_000) return `${(n / 1_000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

function Stat({ value, label, hint, loading }: { value: string; label: string; hint?: string; loading?: boolean }) {
  return (
    <div className="px-4 py-3.5" title={hint}>
      {/* The figure takes the display face and negative tracking: these numbers are the platform's
          strongest claim and they should read as instrument output, not as body copy set larger. */}
      {loading ? (
        // NOT "n/a" while the request is in flight. On this codebase's own convention "n/a" means
        // MEASURED AND UNAVAILABLE, so showing it for the first few seconds told a first-time
        // visitor that the platform cannot measure itself - the opposite of what this strip is for.
        // A skeleton says "still loading", which is the true statement.
        <Skeleton className="h-8 w-20" />
      ) : (
        <div className="font-display text-2xl font-semibold tracking-tight text-text-primary">{value}</div>
      )}
      <div className="mt-1 text-micro uppercase tracking-wide text-text-muted">{label}</div>
    </div>
  );
}

export function LiveStats() {
  const [status, setStatus] = useState<PlatformStatus | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(
    () =>
      api
        .platformStatus()
        .then((s) => {
          setStatus(s);
          setFailed(false);
        })
        .catch(() => setFailed(true)),
    [],
  );

  // Pauses while the tab is hidden. This one used to poll /v1/status every 30s from every open
  // homepage tab, forever, and it is one of THREE independent fetches of that document on this page.
  usePoll(load, 30_000);

  // A failed status call means the figures are unknown, so the strip is omitted entirely. Rendering
  // a row of "n/a" would be honest but useless, and rendering zeros would not be honest at all.
  if (failed && !status) return null;

  // Distinguish the two states the strip can be in. `loading` is "we have not asked yet"; `n/a` is
  // the server answering null, which is a real measurement outcome and must keep saying so.
  const loading = !status && !failed;
  const num = (v: number | null | undefined): string => (typeof v === 'number' ? compact(v) : 'n/a');
  const guests = status?.fleet?.guests;
  const guestValue =
    guests && typeof guests.busy === 'number'
      ? typeof guests.capacity === 'number'
        ? `${guests.busy} / ${guests.capacity}`
        : String(guests.busy)
      : 'n/a';

  return (
    <section aria-label="Platform activity" className="mt-4 grid grid-cols-2 divide-x divide-y divide-border-subtle overflow-hidden rounded-sm border border-border-subtle sm:grid-cols-3 lg:grid-cols-5 lg:divide-y-0">
      <Stat
        loading={loading}
        value={num(status?.corpus?.publicSamples)}
        label="Public reports"
        hint={status?.corpus?.source ?? 'Distinct public reports in the corpus'}
      />
      {/* Labelled "median" because an individual report legitimately shows a different number - the
          engines that ran on THAT sample - and a reader comparing the two should not read the
          difference as an inconsistency. The tooltip carries the server's own description. */}
      <Stat
        loading={loading}
        value={num(status?.engines?.measured)}
        label="Detection engines (median)"
        hint={status?.engines?.source ?? 'Engines that actually ran on recent analyses'}
      />
      <Stat
        loading={loading}
        value={num(status?.submissions?.lastHourThroughput)}
        label="Analyses last hour"
        hint="Analyses completed in the last 60 minutes"
      />
      <Stat
        loading={loading}
        value={guestValue}
        label="Sandbox guests busy"
        hint="Measured from the detonation hosts. n/a means a tier could not be measured, not that it is idle."
      />
      <Stat
        loading={loading}
        value={num(status?.online?.total)}
        label="Online now"
        hint="Registered and anonymous visitors active in the last five minutes"
      />
    </section>
  );
}
